/**
 * Back-navigation and page state for the credit-score drill-down.
 *
 * The pages form a two-level drill-down: a list (TOP 100, forecast, dashboard, the upload summary)
 * → CsPortfolio → CsCustomerDetail. Two things have to travel with the reader: which customer the
 * detail pages are showing, and which list they drilled down from — the back buttons used to call
 * router.go(-1), which walks the history stack one entry at a time rather than going where the
 * reader means. Take one detour — portfolio → "รายละเอียดคะแนน →" → "← Portfolio" — and the stack
 * reads [list, portfolio, detail, portfolio], so "← กลับ" landed on the detail page it had just
 * left instead of on the list.
 *
 * Both ride in `history.state` rather than the query string, which keeps the URL down to a plain
 * /finances/cs_portfolio. The browser stores that state per history entry and hands it back after a
 * reload, so back, forward and refresh all still land on the right customer and know the way home.
 * What it cannot do is travel in a copied link: paste one of these URLs to a colleague and they get
 * the empty state, since there is nothing in the address to say who to show. That is the trade this
 * was chosen for — clean URLs over shareable ones.
 *
 * vue-router carries it for us: `router.push({ name, state })` hands `state` to history.pushState.
 * It is not reactive, so read it during setup — every navigation between these routes remounts the
 * component anyway.
 */

function historyState() {
  return (typeof window !== 'undefined' && window.history.state) || {}
}

function firstOf(value) {
  return Array.isArray(value) ? value[0] : value
}

// ?cus=/?from= are still read so links and bookmarks made before the move to history.state — and
// anything a colleague was sent back then — keep working.
function originOf(route) {
  return firstOf(historyState().from ?? route.query.from)
}

/** Which customer a detail page is showing, '' when nothing says. */
export function detailCustomerId(route) {
  const id = firstOf(historyState().cus ?? route.query.cus)
  return id ? String(id) : ''
}

/** State for a link INTO the drill-down: stamps the list the reader is leaving. */
export function withOrigin(route, state) {
  return { ...state, from: route.fullPath }
}

/** State for a move WITHIN the drill-down: keeps the stamp pointing at the original list. */
export function keepOrigin(route, state) {
  const from = originOf(route)
  return from ? { ...state, from } : { ...state }
}

/**
 * Navigate to `target`, popping instead of pushing when it is exactly the entry we came from.
 * vue-router records that entry's fullPath in history.state.back, so the two can be compared: a
 * real pop keeps the stack honest (no second copy of the page to sit through on the way out), lets
 * the browser's own back button stay in step with the in-page one, and brings back that entry's
 * own state rather than a rebuilt copy.
 */
export function backTo(router, target) {
  const resolved = router.resolve(target)
  const previous = historyState().back
  if (previous === resolved.fullPath) router.go(-1)
  else router.push(target)
}

/** Where a detail page's "← กลับ" should land. */
export function backToOrigin(router, route, fallback = { name: 'CsDashboard' }) {
  const from = originOf(route)
  // `from` can arrive off the URL on an older link, so treat it as untrusted: in-app absolute paths
  // only, and only ones that still resolve — a bookmark can outlive the route it names.
  if (from && from.startsWith('/') && router.resolve(from).matched.length) {
    backTo(router, from)
    return
  }
  // history.state.back is null when this tab has no SPA history to pop — opened in a new tab, or
  // reloaded on this page — where go(-1) would either do nothing or leave the app entirely.
  if (historyState().back) {
    router.go(-1)
    return
  }
  router.push(fallback)
}
